import React, { useCallback, useEffect, useState } from 'react'
import { CCard, CCardBody, CCol, CRow } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilCalendar, cilCheckAlt, cilPeople, cilUser } from '@coreui/icons'
import { useLocation } from 'react-router-dom'
import FixtureVotingService from 'src/service/FixtureVotingService'
import ToastComponent from 'src/components/common/TaostComponent'
import ActivateVotes from './ActivateVotes'
import FixtureVotesTab from './FixtureVotesTab'
import TeamVotesTab from './TeamVotesTab'
import PlayerVotesTable from './PlayerVotesTable'
import LoadError from './LoadError'
import { useVotingRequest } from './votesUtils'
import './Votes.css'

const TABS = [
  { key: 'fixture', label: 'Fixture Votes' },
  { key: 'team', label: 'Team Votes' },
  { key: 'total', label: 'Total Votes' },
]

const Votes = () => {
  const { state } = useLocation()
  const initialTab = TABS.some((t) => t.key === state?.tab) ? state.tab : TABS[0].key
  const [activeTab, setActiveTab] = useState(initialTab)
  const [active, setActive] = useState(null)
  const [activeError, setActiveError] = useState(false)
  const [toggling, setToggling] = useState(false)

  const fixtures = useVotingRequest(FixtureVotingService.getFixtureVotingListing)
  const teams = useVotingRequest(FixtureVotingService.teamFixtureVoting)
  const total = useVotingRequest(FixtureVotingService.overallPlayerVoting)

  const fixtureRows = fixtures.response?.data || []
  const teamRows = teams.response?.data || []
  const playerRows = total.response?.data || []

  const loadStatus = useCallback(() => {
    setActiveError(false)
    FixtureVotingService.getVotingActivation()
      .then((res) => {
        if (res.status === 200) setActive(!!Number(res.data))
        else setActiveError(true)
      })
      .catch(() => setActiveError(true))
  }, [])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  const handleToggle = (checked) => {
    setActive(checked)
    setToggling(true)
    FixtureVotingService.changeVotingStatus(checked)
      .then((res) => {
        if (res.status === 200) {
          ToastComponent(res.message, 'success')
        } else {
          setActive(!checked)
          ToastComponent(res.message, 'error')
        }
      })
      .catch((e) => {
        // Put the switch back - the saved status did not change.
        setActive(!checked)
        ToastComponent(e.response?.data?.message || 'Could not change the status', 'error')
      })
      .finally(() => setToggling(false))
  }

  const counts = {
    fixture: fixtures.loading ? null : fixtureRows.length,
    team: teams.loading ? null : teamRows.length,
    total: total.loading ? null : playerRows.length,
  }

  const STAT_CARDS = [
    { label: 'Status', value: 'Active', icon: cilCheckAlt, color: 'success' },
    {
      label: 'Fixtures Voted On',
      value: counts.fixture ?? '—',
      icon: cilCalendar,
      color: 'primary',
    },
    { label: 'Teams', value: counts.team ?? '—', icon: cilPeople, color: 'info' },
    { label: 'Players Voted For', value: counts.total ?? '—', icon: cilUser, color: 'warning' },
  ]

  return (
    <>
      <div className="ge-page-header">
        <h1 className="ge-page-title">Votes</h1>
        <p className="ge-page-subtitle">
          Turn fixture voting on, then see who members voted for - by fixture, by team, and across
          the whole season.
        </p>
      </div>

      {activeError && (
        <LoadError
          className="mb-3"
          message="We couldn't load whether voting is on. Nothing has been changed."
          onRetry={loadStatus}
        />
      )}

      {active && (
        <CRow className="g-3 mb-3">
          {STAT_CARDS.map((s) => (
            <CCol sm={6} xl={3} key={s.label}>
              <CCard className="h-100">
                <CCardBody className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-medium-emphasis small">{s.label}</div>
                    <div className="fs-4 fw-semibold">{s.value}</div>
                  </div>
                  <div className={`ge-stat-icon ge-stat-icon--${s.color}`}>
                    <CIcon icon={s.icon} size="lg" />
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
          ))}
        </CRow>
      )}

      <CRow className="g-3">
        {!activeError && (
          <CCol xs={12}>
            <ActivateVotes
              active={active}
              loading={active === null}
              toggling={toggling}
              onToggle={handleToggle}
            />
          </CCol>
        )}

        {active && (
          <>
            <CCol xs={12}>
              <div className="ge-tabs mb-0" role="tablist">
                {TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.key}
                    className={`ge-tab${activeTab === tab.key ? ' active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                    {counts[tab.key] !== null && (
                      <span className="ge-tab-count">{counts[tab.key]}</span>
                    )}
                  </button>
                ))}
              </div>
            </CCol>

            <CCol xs={12}>
              <CCard className="vt-panel">
                <CCardBody>
                  {/* Every tab stays mounted so its search, paging and chosen team survive a switch. */}
                  <div className={activeTab === 'fixture' ? undefined : 'd-none'}>
                    <p className="vt-panel-note mb-3">
                      Every fixture members have voted on. Open a row for its vote breakdown, the
                      average, or each member&apos;s votes.
                    </p>
                    <FixtureVotesTab
                      fixtures={fixtureRows}
                      loading={fixtures.loading}
                      error={fixtures.error}
                      onRetry={fixtures.reload}
                    />
                  </div>
                  <div className={activeTab === 'team' ? undefined : 'd-none'}>
                    <p className="vt-panel-note mb-3">
                      Votes each player in a team has received across all of that team&apos;s
                      fixtures.
                    </p>
                    <TeamVotesTab
                      teams={teamRows}
                      loading={teams.loading}
                      error={teams.error}
                      onRetry={teams.reload}
                    />
                  </div>
                  <div className={activeTab === 'total' ? undefined : 'd-none'}>
                    <p className="vt-panel-note mb-3">
                      Every player&apos;s votes across all fixtures, highest total first.
                    </p>
                    <PlayerVotesTable
                      players={playerRows}
                      options={total.response?.votingListing || []}
                      loading={total.loading}
                      error={total.error}
                      onRetry={total.reload}
                      errorMessage="We couldn't load the vote totals."
                      noItemsLabel="No votes yet - players appear here once members start voting"
                    />
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
          </>
        )}
      </CRow>
    </>
  )
}

export default Votes
